import { useQuery } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { CompanySwitcher } from '@/features/company-switch'
import { GlobalSearch } from '@/features/global-search'
import { NotificationsBell } from '@/features/notifications-read'
import { sessionQuery } from '@/entities/session'
import { findService, platformServicesQuery } from '@/entities/platform'
import { ThemeToggle } from '@/features/theme-toggle'
import { Icon, IconButton, Skeleton, WidgetError } from '@/shared/ui'
import { PlatformSwitcher } from './PlatformSwitcher'
import { ProfileMenu } from './ProfileMenu'
import s from './TopBar.module.css'

/**
 * Верхний ярус шапки (prototype/Prototype.dc.html:118-230), 52px, sticky. Слева направо:
 * свитчер сервисов Prima → разделитель → свитчер компаний → поиск → уведомления → шестерёнка
 * Студии → тема → профиль.
 *
 * `sessionQuery` и `platformServicesQuery` читаются здесь, а не внутри каждого поповера:
 * обе нужны больше чем одному месту сразу (сессия — свитчеру компаний и профилю; сервисы
 * платформы — своему свитчеру, кнопке «Сервис «Компании»» в поповере компаний и «Личный
 * кабинет» в профиле), и здесь единственное место, откуда можно раздать результат вниз
 * пропсами, не дублируя сам запрос (хотя TanStack Query задедуплицировал бы и повторные
 * вызовы — просто незачем).
 */
export function TopBar() {
  const {
    data: session,
    error: sessionError,
    refetch: refetchSession,
  } = useQuery(sessionQuery())
  const {
    data: services,
    error: servicesError,
    refetch: refetchServices,
  } = useQuery(platformServicesQuery())

  const navigate = useNavigate()
  const studioActive = useRouterState({
    select: (routerState) => routerState.location.pathname.startsWith('/studio'),
  })

  const companiesService = findService(services, 'companies')
  const personalAccountUrl = findService(services, 'id')?.url

  return (
    // `div`, а не `header`: landmark `banner` — один на всю шапку, и его объявляет `AppHeader`
    // (общая липкая обёртка обоих ярусов). Вложенный `header` дал бы второй `banner`, а два
    // одноимённых ориентира в скринридере — это уже не навигация, а угадайка.
    <div className={s.root}>
      <PlatformSwitcher
        services={services}
        error={servicesError}
        onRetry={() => void refetchServices()}
      />

      <span className={s.divider} aria-hidden="true" />

      {/*
       * ДАННЫЕ ПРОВЕРЯЮТСЯ ПЕРВЫМИ, а не ошибка. Это не стилистика — обратный порядок ломает
       * шапку на всех экранах сразу.
       *
       * После упавшего ФОНОВОГО перезапроса (а он идёт при каждом возврате во вкладку —
       * `refetchOnWindowFocus: true`) TanStack Query выставляет `error` И ОСТАВЛЯЕТ `data`.
       * Проверено зондом на живой библиотеке:
       *
       *   status=error  isError=true  data="хорошие данные"  error={…503}
       *
       * С проверкой ошибки впереди человек, вернувшийся во вкладку в момент, когда `/me` икнул
       * пятисоткой, увидел бы вместо свитчера компаний «Сервер не смог ответить» — при том что
       * данные целы и меню профиля в двух сантиметрах правее продолжает показывать его же.
       * Ошибку показываем, только если показать НЕЧЕГО.
       */}
      {session ? (
        session.company && (
          <CompanySwitcher
            activeCompany={session.company}
            footer={
              companiesService?.url && (
                <a href={companiesService.url} className={s.companiesServiceCta}>
                  <span className={s.companiesServiceText}>
                    <span>Сервис «Компании»</span>
                    <span className={s.companiesServiceSlug}>{companiesService.slug}</span>
                  </span>
                  <Icon name="external" size={15} strokeWidth={2} />
                </a>
              )
            }
          />
        )
      ) : sessionError ? (
        <div className={s.sessionError}>
          <WidgetError error={sessionError} retry={() => void refetchSession()} />
        </div>
      ) : (
        <div className={s.sessionSkeleton}>
          <Skeleton width={24} height={24} radius={5} />
          <Skeleton width={120} height={14} radius={4} />
        </div>
      )}

      <GlobalSearch />

      <div className={s.actions}>
        <NotificationsBell />

        {/* 17/1.7 — prototype/Prototype.dc.html:207. */}
        <IconButton
          icon="gear"
          title="Настройка и управление"
          size={17}
          strokeWidth={1.7}
          active={studioActive}
          onClick={() => void navigate({ to: '/studio' })}
        />

        <ThemeToggle />

        {session && (
          <ProfileMenu
            user={session.user}
            companyName={session.company?.name}
            personalAccountUrl={personalAccountUrl}
          />
        )}
      </div>
    </div>
  )
}
